"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { storyblokEditable } from "@storyblok/react/rsc";

import { Button } from "@/components/ui/button";
import { EventTag } from "@/components/layout/EventTag";
import { MemberLockIcon, LockedIcon } from "@/components/icons/MemberLockIcon";
import { MaskShape, type MaskShapeVariant } from "@/components/illustrations/MaskShape";
import { GridContainer } from "@/components/layout/GridContainer";
import { EventPreviewModal, type EventPreviewData } from "@/components/landing/EventPreviewModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import type { EventStoryblok } from "@/component-types-sb";
import { expandToOccurrences, formatDateParam } from "@/lib/utils/recurrence";
import { formatEventDateRange } from "@/lib/utils/date";
import { cn } from "@/lib/utils";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import { EventSignupModal, type EventSignupData } from "@/components/events/EventSignupModal";
import { ArrowRightThin } from "@/components/icons";
import { useMembership } from "@/lib/hooks/useMembership";
import { eventTypeFromContent } from "@/lib/utils/eventType";
import {
  fetchStoryblokList,
  fetchEventsAndTravels,
} from "@/lib/storyblok/fetchList";

type SingleEventCardStoryblok = {
  title?: string;
  event?: string | { content: any; full_slug: string; [k: string]: any };
  category?: string;
  tag?: string;
  button_label?: string;
  _uid: string;
  component: "single_event_card";
  [k: string]: any;
};

type SingleEventCardProps = {
  blok: SingleEventCardStoryblok;
  inline?: boolean;
};

type EventData = {
  title: string;
  date: Date;
  endDate?: Date;
  formattedDate: string;
  eventType: string;
  image?: { src: string; alt: string };
  maskShape: MaskShapeVariant;
  ctaLink: string;
  memberOnly: boolean;
  /** Fuer das Anmelde-Modal — dieselben Felder, die EventGallery liefert. */
  uuid: string;
  slug: string;
  originalDateIso: string;
  eventLink?: string;
  description?: any;
  experts?: { name: string; image?: { filename: string; alt?: string } }[];
};

function parseEventStory(story: { content: any; full_slug: string; uuid?: string }): EventData | null {
  const occurrences = expandToOccurrences(story);
  if (occurrences.length === 0) return null;

  const occ = occurrences[0];
  const content = story.content as EventStoryblok;
  const formattedDate = occ.endDate
    ? formatEventDateRange(occ.displayDate, occ.endDate)
    : format(occ.displayDate, "EEEE, dd. MMM yyyy", { locale: de });

  return {
    title: content.title,
    date: occ.displayDate,
    endDate: occ.endDate,
    formattedDate,
    eventType: eventTypeFromContent(content),
    image: content.image?.filename
      ? { src: content.image.filename, alt: content.image.alt || content.title }
      : undefined,
    maskShape: (content.image_mask_shape || "four_vertical_even") as MaskShapeVariant,
    ctaLink: occ.isRepeating
      ? `/${story.full_slug}?date=${formatDateParam(occ.displayDate)}`
      : `/${story.full_slug}`,
    memberOnly: content.member_access === "member_only",
    uuid: (story as { uuid?: string }).uuid ?? "",
    slug: `/${story.full_slug}`,
    originalDateIso: occ.originalDate.toISOString(),
    eventLink: getUrlFromLinkObject(content.link) || undefined,
    description: content.text,
    experts: ((content as any).experts || [])
      .filter((e: any) => typeof e !== "string" && e?.content)
      .map((e: any) => ({
        name: e.content.name,
        image: e.content.image,
      })),
  };
}

const SingleEventCard = ({ blok, inline }: SingleEventCardProps) => {
  const isLanding = useIsLandingPage();
  const [previewEvent, setPreviewEvent] = useState<EventPreviewData | null>(null);
  const [signupEvent, setSignupEvent] = useState<EventSignupData | null>(null);
  const { isMember } = useMembership();

  const [event, setEvent] = useState<EventData | null>(() => {
    // Use resolved event from server if available
    if (blok.event && typeof blok.event !== "string" && "content" in blok.event) {
      return parseEventStory(blok.event);
    }
    return null;
  });

  const category = blok.category;
  const tag = blok.tag;
  const hasResolvedEvent = blok.event && typeof blok.event !== "string" && "content" in blok.event;
  const eventId = typeof blok.event === "string" ? blok.event : undefined;

  useEffect(() => {
    if (hasResolvedEvent) return;
    if (!category && !tag && !eventId) return;

    const fetchNextEvent = async () => {
      try {
        if (eventId) {
          // Das Referenzfeld erlaubt Events und Reisen — welcher Typ hinter
          // der UUID steckt, weiss die Karte nicht. Beide abfragen; der
          // falsche Typ liefert einfach nichts.
          const byUuid = await fetchEventsAndTravels({
            by_uuids: eventId,
          });
          if (byUuid?.[0]) {
            const parsed = parseEventStory(byUuid[0]);
            if (parsed) {
              setEvent(parsed);
              return;
            }
          }
        }

        // Reisen sind ein eigener Content-Type, sollen hier aber wie Events
        // gefunden werden — geladen wird beides.
        const filterQuery: Record<string, any> = {};
        // `category` gibt es nur an Events; als gemeinsamer Filter wuerde er
        // alle Reisen aussortieren.
        const eventOnlyFilter = category
          ? { category: { in: category } }
          : undefined;
        // any_in_array statt in: tags ist ein Array-Feld
        // (component-types-sb.d.ts), fuer das `in` als Skalar-Operator nicht
        // greift. So machen es EventGallery und ArticleGallery auch.
        if (tag) filterQuery.tags = { any_in_array: tag };
        if (!category && !tag) return;

        // Der Kategoriefilter wirkt nur auf die event-Haelfte. Waere `travel`
        // immer dabei, holte eine Karte mit category="community" auch alle
        // Reisen — und weil unten der naechstliegende Termin gewinnt, koennte
        // sie statt des Community-Events eine Reise anzeigen. Reisen also nur
        // laden, wenn die Karte sie ueberhaupt meint.
        const wantsTravel = !category || category === "reisen";

        const stories = wantsTravel
          ? await fetchEventsAndTravels({
              filter_query: filterQuery,
              eventOnlyFilter,
              per_page: 25,
            })
          : await fetchStoryblokList({
              content_type: "event",
              filter_query: { ...filterQuery, ...eventOnlyFilter },
              per_page: 25,
            });

        if (stories && stories.length > 0) {
          let nearest: EventData | null = null;
          for (const story of stories) {
            const parsed = parseEventStory(story);
            if (parsed && (!nearest || parsed.date < nearest.date)) {
              nearest = parsed;
            }
          }
          if (nearest) setEvent(nearest);
        }
      } catch (error) {
        console.error("Error fetching next event:", error);
      }
    };

    fetchNextEvent();
  }, [category, tag, eventId]);

  if (!event) return null;

  // Wie bei EventCard: immer "Jetzt anmelden", die Farbe traegt den Zugang.
  // Reisen bleibt die Ausnahme — eine Reise bucht man nicht mit einem Klick.
  const ctaLabel = blok.button_label
    || (event.eventType === "reisen" ? "Mehr erfahren" : "Jetzt anmelden");

  /**
   * Dieselben zwei Knoepfe wie auf der grossen Event-Karte: primaer die
   * Anmeldung in der Zugangsfarbe, sekundaer ein Textlink zur Event-Seite.
   *
   * Vorher war die ganze Karte ein Link und der Knopf reine Optik
   * (`pointer-events-none`). Zwei Ziele verlangen zwei Knoepfe, also ist die
   * Karte nicht mehr als Ganzes klickbar.
   */
  const cardActions = (
    <div className="flex flex-col items-center gap-3 w-full">
      <Button
        variant={event.memberOnly ? "sun" : "black"}
        className="w-full"
        onClick={() => setSignupEvent({
          storyblokEventUuid: event.uuid,
          eventDate: event.originalDateIso,
          eventDisplayDate: event.date.toISOString(),
          eventTitle: event.title,
          eventSlug: event.slug,
          eventType: event.eventType,
          eventLink: event.eventLink,
          memberOnly: event.memberOnly,
          returnUrl: event.ctaLink,
        })}
      >
        {ctaLabel}
        {event.memberOnly && !isMember && <LockedIcon className="size-4 text-black shrink-0" />}
      </Button>
      <Link
        href={event.ctaLink}
        className="inline-flex items-center gap-2 text-copy text-black hover:opacity-70 transition-opacity"
      >
        Zum Event
        <ArrowRightThin className="size-4" />
      </Link>
    </div>
  );

  const openPreview = () => {
    setPreviewEvent({
      title: event.title,
      date: event.date,
      endDate: event.endDate,
      eventType: event.eventType,
      image: event.image,
      memberOnly: event.memberOnly,
      description: event.description,
      experts: event.experts,
    });
    trackEvent("content_preview_opened", {
      type: "event",
      title: event.title,
      source: "single_event_card",
      eventType: event.eventType,
    });
  };

  const card = (
    <div className={cn("flex flex-col gap-4 items-center", !inline && "col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 px-4 md:px-0")}>
      {blok.title && (
        <h2 className="text-headline-xl text-black text-center">
          {blok.title}
        </h2>
      )}
      {isLanding ? (
        <div className="bg-white rounded-3xl p-3 flex flex-col gap-6 w-full card-rotate cursor-pointer" onClick={openPreview} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openPreview()}>
          {/* Mobile: stacked layout */}
          <div className="flex flex-col gap-4 md:hidden">
            {event.image ? (
              <div className="relative w-full h-[167px] rounded-2xl overflow-hidden">
                <Image
                  src={event.image.src}
                  alt={event.image.alt}
                  fill
                  sizes="100vw"
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="relative w-full h-[167px] overflow-hidden">
                <MaskShape
                  variant={event.maskShape}
                  className="w-full h-full text-sun-100"
                  preserveAspectRatio="none"
                />
              </div>
            )}

            <div className="flex flex-col gap-3 items-center text-center">
              <div className="flex items-center gap-1">
                {event.eventType && <EventTag variant={event.eventType} memberOnly={event.memberOnly} />}
                {event.memberOnly && <MemberLockIcon locked />}
              </div>
              <div className="flex flex-col">
                <p className="text-tag text-gray-100">{event.formattedDate}</p>
                <p className="text-headline-md-static text-black">{event.title}</p>
              </div>
            </div>
          </div>

          {/* Tablet+: horizontal layout */}
          <div className="hidden md:flex md:flex-row gap-8 items-center">
            {event.image ? (
              <div className="relative w-[240px] h-[167px] rounded-2xl overflow-hidden shrink-0">
                <Image
                  src={event.image.src}
                  alt={event.image.alt}
                  fill
                  sizes="240px"
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="relative w-[240px] h-[167px] overflow-hidden shrink-0">
                <MaskShape
                  variant={event.maskShape}
                  className="w-full h-full text-sun-100"
                  preserveAspectRatio="none"
                />
              </div>
            )}

            <div className="flex flex-col gap-4 items-start text-left flex-1">
              <div className="flex items-center gap-1">
                {event.eventType && <EventTag variant={event.eventType} memberOnly={event.memberOnly} />}
                {event.memberOnly && <MemberLockIcon locked />}
              </div>
              <div className="flex flex-col">
                <p className="text-tag text-gray-100">{event.formattedDate}</p>
                <p className="text-headline-md-static text-black">{event.title}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-3 flex flex-col gap-6 w-full card-rotate">
          {/* Mobile: stacked layout */}
          <div className="flex flex-col gap-4 md:hidden">
            {event.image ? (
              <div className="relative w-full h-[167px] rounded-2xl overflow-hidden">
                <Image
                  src={event.image.src}
                  alt={event.image.alt}
                  fill
                  sizes="100vw"
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="relative w-full h-[167px] overflow-hidden">
                <MaskShape
                  variant={event.maskShape}
                  className="w-full h-full text-sun-100"
                  preserveAspectRatio="none"
                />
              </div>
            )}

            <div className="flex flex-col gap-3 items-center text-center">
              <div className="flex items-center gap-1">
                {event.eventType && <EventTag variant={event.eventType} memberOnly={event.memberOnly} />}
                {event.memberOnly && <MemberLockIcon locked />}
              </div>
              <div className="flex flex-col">
                <p className="text-tag text-gray-100">{event.formattedDate}</p>
                <p className="text-headline-md-static text-black">{event.title}</p>
              </div>
            </div>

            {cardActions}
          </div>

          {/* Tablet+: horizontal layout */}
          <div className="hidden md:flex md:flex-row gap-8 items-center">
            {event.image ? (
              <div className="relative w-[240px] h-[167px] rounded-2xl overflow-hidden shrink-0">
                <Image
                  src={event.image.src}
                  alt={event.image.alt}
                  fill
                  sizes="240px"
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="relative w-[240px] h-[167px] overflow-hidden shrink-0">
                <MaskShape
                  variant={event.maskShape}
                  className="w-full h-full text-sun-100"
                  preserveAspectRatio="none"
                />
              </div>
            )}

            <div className="flex flex-col gap-4 items-start text-left flex-1">
              <div className="flex items-center gap-1">
                {event.eventType && <EventTag variant={event.eventType} memberOnly={event.memberOnly} />}
                {event.memberOnly && <MemberLockIcon locked />}
              </div>
              <div className="flex flex-col">
                <p className="text-tag text-gray-100">{event.formattedDate}</p>
                <p className="text-headline-md-static text-black">{event.title}</p>
              </div>
              {cardActions}
            </div>
          </div>
        </div>
      )}
      {previewEvent && (
        <EventPreviewModal event={previewEvent} onClose={() => setPreviewEvent(null)} />
      )}
      {signupEvent && (
        <EventSignupModal
          open={!!signupEvent}
          onOpenChange={(open) => !open && setSignupEvent(null)}
          event={signupEvent}
        />
      )}
    </div>
  );

  if (inline) {
    return (
      <div className="mt-4 md:mt-6 mb-4 md:mb-6">
        {card}
      </div>
    );
  }

  return (
    <GridContainer {...storyblokEditable(blok)} className="!px-0 mt-4 md:mt-6 mb-4 md:mb-6">
      {card}
    </GridContainer>
  );
};

export default SingleEventCard;
